export class PrintHelperUnavailableError extends Error {
  constructor() {
    super('PRINT_HELPER_UNAVAILABLE');
    this.name = 'PrintHelperUnavailableError';
  }
}

export type TicketPrintLinePayload =
  | { type: 'center'; text: string }
  | { type: 'left'; text: string }
  | { type: 'row'; left: string; right: string; bold?: boolean }
  | { type: 'rule' }
  | { type: 'blank' };

function getPrintHelperUrl(): string {
  const fromEnv = process.env.REACT_APP_PRINT_HELPER_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'http://127.0.0.1:9123';
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

function linesToPlainText(lines: TicketPrintLinePayload[]): string {
  return lines
    .map((line) => {
      if (line.type === 'center' || line.type === 'left') return line.text;
      if (line.type === 'row') {
        if (line.left && line.right) return `${line.left}  ${line.right}`;
        return line.right || line.left;
      }
      if (line.type === 'rule') return '--------------------------------';
      return '';
    })
    .join('\n');
}

export async function printTicketLines(
  lines: TicketPrintLinePayload[],
  printerName?: string | null,
): Promise<void> {
  const baseUrl = getPrintHelperUrl();

  let response: Response;
  try {
    response = await fetchWithTimeout(
      `${baseUrl}/print`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines,
          text: linesToPlainText(lines),
          printerName: printerName?.trim() || undefined,
        }),
      },
      15000,
    );
  } catch {
    throw new PrintHelperUnavailableError();
  }

  if (!response.ok) {
    let message = `Error del asistente de impresión (${response.status})`;
    try {
      const data = (await response.json()) as { message?: string };
      if (data.message?.trim()) message = data.message.trim();
    } catch {
      /* ignore */
    }
    if (message.includes('Falta el texto') || message.includes('Faltan líneas')) {
      message += '. Reiniciá eOne Print Helper (tools/eone-print-helper → dotnet run).';
    }
    throw new Error(message);
  }
}
